import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  useGetSessionQuery,
  type SessionMessage,
  type ThinkingStep,
  type ActionEvent,
} from '../../store/api';

/* ---------- styled components (unchanged from original) ---------- */
const PageBody = styled.div`
  height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SessionHeader = styled.div`
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid var(--border);
  background: var(--bg-surface);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const BackLink = styled.a`
  color: var(--text-muted);
  font-size: 1.125rem;
  cursor: pointer;
`;

const HeaderInfo = styled.div`
  h2 {
    font-size: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const SessionSub = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const Badge = styled.span<{ $variant?: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 500;
  background: ${({ $variant }) => $variant === 'green' ? 'var(--success-subtle)' : $variant === 'red' ? 'var(--error-subtle)' : 'var(--accent-subtle)'};
  color: ${({ $variant }) => $variant === 'green' ? 'var(--success)' : $variant === 'red' ? 'var(--error)' : 'var(--accent)'};
`;

const Btn = styled.button<{ $variant?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: ${({ $variant }) => $variant === 'secondary' ? '1px solid var(--border)' : 'none'};
  background: ${({ $variant }) => $variant === 'secondary' ? 'var(--bg-elevated)' : 'transparent'};
  color: var(--text-secondary);
  &:hover { background: var(--bg-elevated); }
`;

const AISummaryBanner = styled.div`
  padding: 0.875rem 1.5rem;
  background: var(--accent-subtle);
  border-bottom: 1px solid var(--accent-muted);
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const AISummaryIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: var(--accent);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
`;

const AISummaryText = styled.div`
  font-size: 0.8125rem;
  color: var(--accent-text);
  line-height: 1.5;
`;

const AISummaryLabel = styled.span`
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.6875rem;
  letter-spacing: 0.04em;
  display: block;
  margin-bottom: 0.25rem;
`;

const ReplayLayout = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  overflow: hidden;
  @media (max-width: 1024px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Lane = styled.div<{ $hidden?: boolean }>`
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--border);
  overflow: hidden;
  &:last-child { border-right: none; }
  @media (max-width: 768px) {
    display: ${({ $hidden }) => $hidden ? 'none' : 'flex'};
  }
`;

const LaneHeader = styled.div`
  padding: 0.625rem 1rem;
  border-bottom: 1px solid var(--border);
  background: var(--bg-surface);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const LaneContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`;

const ConvMsg = styled.div<{ $role: string }>`
  display: flex;
  gap: 0.625rem;
  margin-bottom: 1.25rem;
  ${({ $role }) => $role === 'user' ? 'flex-direction: row-reverse;' : ''}
`;

const ConvAvatar = styled.div<{ $role: string }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  font-weight: 600;
  flex-shrink: 0;
  background: ${({ $role }) => $role === 'user' ? 'var(--bg-elevated)' : 'var(--accent-subtle)'};
  color: ${({ $role }) => $role === 'user' ? 'var(--text-secondary)' : 'var(--accent)'};
`;

const ConvBubble = styled.div<{ $role: string }>`
  padding: 0.75rem 1rem;
  border-radius: 12px;
  font-size: 0.8125rem;
  line-height: 1.6;
  max-width: 90%;
  ${({ $role }) => $role === 'user'
    ? 'background: var(--accent); color: white; border-top-right-radius: 4px;'
    : 'background: var(--bg-elevated); color: var(--text-secondary); border-top-left-radius: 4px;'}
`;

const ConvTime = styled.div`
  font-size: 0.6875rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
`;

const ThoughtItem = styled.div`
  margin-bottom: 1.25rem;
  padding: 0.875rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  font-size: 0.8125rem;
`;

const ThoughtStep = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
`;

const StepNum = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--accent-subtle);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  font-weight: 700;
`;

const ThoughtText = styled.div`
  color: var(--text-secondary);
  line-height: 1.5;
`;

const ActionItem = styled.div`
  margin-bottom: 1rem;
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
`;

const ActionIcon = styled.div<{ $type: string }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  flex-shrink: 0;
  background: ${({ $type }) =>
    $type === 'delegation' ? 'var(--info-subtle)'
    : $type === 'task_execution' ? 'var(--warning-subtle)'
    : $type === 'success' ? 'var(--success-subtle)'
    : $type === 'rag_query' ? 'var(--accent-subtle)'
    : 'var(--error-subtle)'};
  color: ${({ $type }) =>
    $type === 'delegation' ? 'var(--info)'
    : $type === 'task_execution' ? 'var(--warning)'
    : $type === 'success' ? 'var(--success)'
    : $type === 'rag_query' ? 'var(--accent)'
    : 'var(--error)'};
`;

const ActionTitle = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.125rem;
`;

const ActionDesc = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  line-height: 1.4;
`;

const ActionTime = styled.div`
  font-size: 0.6875rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
`;

const LaneTabs = styled.div`
  display: none;
  border-bottom: 1px solid var(--border);
  background: var(--bg-surface);
  @media (max-width: 768px) { display: flex; }
`;

const LaneTab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.75rem;
  text-align: center;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ $active }) => $active ? 'var(--accent)' : 'var(--text-muted)'};
  cursor: pointer;
  border: none;
  border-bottom: 2px solid ${({ $active }) => $active ? 'var(--accent)' : 'transparent'};
  background: none;
  font-family: inherit;
  transition: all 0.2s;
`;

const LoadingContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
`;

type LaneName = 'conversation' | 'thinking' | 'actions';

const ACTION_ICON_MAP: Record<string, string> = {
  api_call: '\u2699',
  rag_query: '\uD83D\uDCC1',
  delegation: '\uD83D\uDD04',
  task_execution: '\u2699',
  error: '\u26A0',
};

/* ---------- component ---------- */
const SessionReplay: React.FC = () => {
  const { agentId, id } = useParams<{ agentId: string; id: string }>();
  const navigate = useNavigate();
  const [activeLane, setActiveLane] = useState<LaneName>('conversation');

  const { data: session, isLoading, isError } = useGetSessionQuery(id ?? '');

  if (isLoading) return <PageBody><LoadingContainer>Loading session...</LoadingContainer></PageBody>;
  if (isError || !session) return <PageBody><LoadingContainer>Failed to load session.</LoadingContainer></PageBody>;

  const statusBadge = session.status === 'success' ? 'green' : session.status === 'failed' ? 'red' : 'teal';
  const statusLabel = session.status === 'success' ? 'Success' : session.status === 'failed' ? 'Failed' : 'In Progress';

  return (
    <PageBody>
      <SessionHeader>
        <HeaderLeft>
          <BackLink onClick={() => navigate(`/agents/${agentId}`)} title="Back">&larr;</BackLink>
          <HeaderInfo>
            <h2>
              {session.agentName} &middot;{' '}
              <span style={{ fontFamily: "'SF Mono', monospace", color: 'var(--text-muted)', fontSize: '0.8125rem', fontWeight: 400 }}>
                #{session.sessionId.slice(0, 8)}
              </span>{' '}
              <Badge $variant={statusBadge}>{statusLabel}</Badge>
            </h2>
            <SessionSub>
              {new Date(session.createdAt).toLocaleString()} &middot;{' '}
              {session.userId} &middot;{' '}
              {session.messageCount} messages &middot;{' '}
              {(session.totalDurationMs / 1000).toFixed(1)}s total
            </SessionSub>
          </HeaderInfo>
        </HeaderLeft>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Btn onClick={() => navigate(`/agents/${agentId}`)}>&#x1F504; Replay in Playground</Btn>
          <Btn $variant="secondary">&#x2B07; Export</Btn>
        </div>
      </SessionHeader>

      {session.summary && (
        <AISummaryBanner>
          <AISummaryIcon>AI</AISummaryIcon>
          <AISummaryText>
            <AISummaryLabel>Session Summary</AISummaryLabel>
            {session.summary}
          </AISummaryText>
        </AISummaryBanner>
      )}

      <LaneTabs>
        {(['conversation', 'thinking', 'actions'] as LaneName[]).map((lane) => (
          <LaneTab key={lane} $active={activeLane === lane} onClick={() => setActiveLane(lane)}>
            {lane.charAt(0).toUpperCase() + lane.slice(1)}
          </LaneTab>
        ))}
      </LaneTabs>

      <ReplayLayout>
        {/* Conversation */}
        <Lane $hidden={activeLane !== 'conversation'}>
          <LaneHeader>&#x1F4AC; Conversation</LaneHeader>
          <LaneContent>
            {(session.conversation ?? []).map((msg: SessionMessage, i: number) => (
              <ConvMsg key={i} $role={msg.role}>
                <ConvAvatar $role={msg.role}>
                  {msg.role === 'user' ? 'U' : session.agentName[0]}
                </ConvAvatar>
                <div>
                  <ConvBubble $role={msg.role}>{msg.content}</ConvBubble>
                  <ConvTime>{new Date(msg.timestamp).toLocaleTimeString()}</ConvTime>
                </div>
              </ConvMsg>
            ))}
            {(!session.conversation || session.conversation.length === 0) && (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem 0' }}>
                No conversation data available.
              </div>
            )}
          </LaneContent>
        </Lane>

        {/* Thinking */}
        <Lane $hidden={activeLane !== 'thinking'}>
          <LaneHeader>&#x1F9E0; Agent Thinking</LaneHeader>
          <LaneContent>
            {(session.thinkingTrace ?? []).map((step: ThinkingStep, i: number) => (
              <ThoughtItem key={step.id || i}>
                <ThoughtStep>
                  <StepNum>{i + 1}</StepNum>
                  {step.type}
                </ThoughtStep>
                <ThoughtText>{step.title}</ThoughtText>
                {step.detail && (
                  <div style={{
                    marginTop: '0.5rem', padding: '0.5rem 0.75rem',
                    background: 'var(--code-bg)', borderRadius: '6px',
                    fontFamily: "'SF Mono', 'Fira Code', monospace",
                    fontSize: '0.75rem', color: 'var(--text-muted)',
                    maxHeight: 100, overflowY: 'auto', whiteSpace: 'pre-wrap',
                  }}>
                    {step.detail}
                  </div>
                )}
              </ThoughtItem>
            ))}
            {(!session.thinkingTrace || session.thinkingTrace.length === 0) && (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem 0' }}>
                No thinking trace available.
              </div>
            )}
          </LaneContent>
        </Lane>

        {/* Actions */}
        <Lane $hidden={activeLane !== 'actions'}>
          <LaneHeader>&#x26A1; Actions &amp; Tasks</LaneHeader>
          <LaneContent>
            {(session.actionsLog ?? []).map((action: ActionEvent, i: number) => (
              <ActionItem key={action.id || i}>
                <ActionIcon $type={action.type}>
                  {ACTION_ICON_MAP[action.type] || '\u2699'}
                </ActionIcon>
                <div>
                  <ActionTitle>{action.title}</ActionTitle>
                  <ActionDesc>{action.detail}</ActionDesc>
                  <ActionTime>
                    {new Date(action.timestamp).toLocaleTimeString()}
                    {action.duration ? ` \u2014 ${action.duration}ms` : ''}
                  </ActionTime>
                </div>
              </ActionItem>
            ))}
            {(!session.actionsLog || session.actionsLog.length === 0) && (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem 0' }}>
                No actions log available.
              </div>
            )}
          </LaneContent>
        </Lane>
      </ReplayLayout>
    </PageBody>
  );
};

export default SessionReplay;
